import { ChangeDetectionStrategy, Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { PingComponent } from '@/pages/tools/ping/ping.component';
import { PING_SERVICE } from 'src/api/ping.service.interface';
import { CloudPingService } from '@/cloud/services/cloud-ping.service';
import { DeviceContextService } from '../device-context.service';

/**
 * Cloud wrapper for the Ping tool.
 * Provides CloudPingService (pre-loaded with deviceId) to the shared PingComponent via the
 * PING_SERVICE injection token. The header comes from the Device shell, so it is suppressed here.
 */
@Component({
    selector: 'app-cloud-ping',
    standalone: true,
    imports: [PingComponent],
    template: `
        @if (ready()) {
            <app-ping [showHeader]="false" />
        }
    `,
    styles: [
        `
            /* The device shell puts children in a flex column, so claim the row — without this
               the host defaults to display:block and app-ping's own flex:1 is inert. */
            :host {
                display: flex;
                flex-direction: column;
                flex: 1;
                min-height: 0;
            }
        `
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [CloudPingService, { provide: PING_SERVICE, useExisting: CloudPingService }]
})
export class CloudPingComponent implements OnInit {
    private route = inject(ActivatedRoute);
    private deviceCtx = inject(DeviceContextService);
    private pingService = inject(CloudPingService);

    ready = signal(false);

    ngOnInit(): void {
        // 'ping' is a non-empty child path, so deviceId lives on the parent route; fall back
        // to the shell's device context.
        const deviceId = this.route.snapshot.paramMap.get('deviceId') ?? this.route.parent?.snapshot.paramMap.get('deviceId') ?? this.deviceCtx.device()?.id ?? '';
        if (deviceId) {
            this.pingService.setDeviceId(deviceId);
            this.ready.set(true);
        }
    }
}
